import moment from "moment";
import {
  isBusinessDay,
  nextBusinessDay,
  businessAdd,
} from "@/helpers/momentBusinessDays";
import { getCampaignLaunchBufferDays } from "@/helpers/utils";
import { logger } from "@/helpers/loggerHelper";
import { CAMPAIGN_CONST, DATE_CONST } from "@/config/constants";
import min from "lodash/min";
import max from "lodash/max";
import {
  buildChannelSelectionData,
  buildSelectedPackageCreativeData,
  getDefaultSelectedChannelIds,
  getDraftSelectedChannelIds,
  getPackageChannelOptions,
  isParticipantChannelSelectionEnabled,
} from "./channelSelectionHelper";
import {
  buildBudgetRecommendationState,
  getBudgetRecommendationSubmitData,
  isBudgetRecommendationEnabled,
} from "./budgetRecommendationHelper";
// import { getCreditCardProcessingFee } from "../config/appConfig";

export const joinCampaignHelper = {
  getValidLocations,
  getTotal,
  isAllLocationsAreNotOrPartiallyFilled,
  isNormalCampaignConstraintViolated,
  isEvergreenCampaignConstraintViolated,
  getFormattedLocationData,
  getInitialValues,
  getBrandContributionAmount,
  getTotalCalculations,
  getRequiredMinMaxFactor,
  getRequiredMinMax,
  // canChangeBudget,
  canChangeDate,
  validateMinCreativeSelection,
  getPostCardValidLocations,
  getPostCardLocTotal,
  getPostCardCampFormattedLocationData,
  getLocAddress,
  getPostCardCampInitialValues,
  isPostCardCostFactorExist,
  isCampaignAdvancedSettingsViolated,
  isFullyFilledLocation,
  isFullyFilledPostCardLocation,
  getCampaignMinMaxStartDate,
  getCampaignMinMaxEndDate,
  validateEvergreenCampLocForLocSpecBudgetType,
  getCollectedMinMaxStartEndDate,
  hideField,
  isFullyFilledValidLocation,
};

/**
 * @method isFullyFilledLocation
 * @description check is the location is filled with required details so, consider it as filled
 * @param {*} locData
 * @param {*} budget_type
 * @returns
 */
function isFullyFilledLocation(locData, budget_type) {
  const filledBudget =
    budget_type === "Pre-set" && parseFloat(locData?.budget) === 0
      ? true
      : Number(locData?.budget);

  return (
    filledBudget &&
    locData?.creatives &&
    locData?.creatives?.length &&
    locData?.start_date &&
    locData?.end_date
  );
}

function isFullyFilledValidLocation(locData, campaign) {
  return (
    isFullyFilledLocation(locData, campaign?.budget_type) &&
    !getRequiredMinMax(locData, campaign)
  );
}

/**
 * @method isFullyFilledPostCardLocation
 * @description check is a post card location is fully filled with the required details
 * @param {*} locData
 * @returns
 */
function isFullyFilledPostCardLocation(locData) {
  return (
    locData?.creatives &&
    locData?.creatives?.length &&
    ((locData?.isMailing && locData?.mailingCost) ||
      (locData?.isBulkDrop && locData?.bulkDropCost))
  );
}

/**
 * @method getValidLocations
 * @description return locations & locations ids which has all data filled. i.e. Has Budget, Start Date, End Date & at least one selected creative
 * @param {*} values
 * @param {*} returnLocationsAsArray
 * @returns
 */
function getValidLocations(
  values = {},
  campaign,
  returnLocationsAsArray = true
) {
  const locs = Object.keys(values);
  if (locs.length === 0)
    return {
      locIds: [],
      locs: returnLocationsAsArray ? [] : {},
    };

  const fullyFilledLocationsIds = locs.length
    ? locs.filter((key) => {
        if (isFullyFilledValidLocation(values[key], campaign)) {
          return true;
        }
        return false;
      })
    : [];

  let fullyFilledLocations = {
    ...fullyFilledLocationsIds.reduce(
      (acc, key) => ({ ...acc, [key]: { ...values[key], locationId: key } }),
      {}
    ),
  };

  if (returnLocationsAsArray) {
    fullyFilledLocations = Object.values(fullyFilledLocations);
  }

  return {
    locIds: fullyFilledLocationsIds,
    locs: fullyFilledLocations,
  };
}

/**
 * @method getTotal
 * @description return sum of the locations budget
 * @param {*} values
 * @returns
 */
function getTotal(values = {}) {
  const locs = Array.isArray(values) ? values : Object.values(values);

  let budgetTotal = 0.0;
  let brandContributionTotal = 0.0;
  if (locs.length) {
    budgetTotal = locs.reduce(
      (t, v) => (Number(v.budget) ? parseFloat(v.budget) + t : t),
      0.0
    );
    brandContributionTotal = locs.reduce(
      (t, v) =>
        Number(v.brand_contribution_amount)
          ? parseFloat(v.brand_contribution_amount) + t
          : t,
      0.0
    );
  }
  return budgetTotal + brandContributionTotal;
}

/**
 * @method isAllLocationsAreNotOrPartiallyFilled
 * @description check all locations whether they are partially or not filled at all and return true or false
 * @param {*} values
 * @returns
 */
function isAllLocationsAreNotOrPartiallyFilled(values = {}) {
  const locs = Array.isArray(values) ? values : Object.values(values);
  return locs.every((l) => {
    if (
      !Number(l.budget) ||
      !l.creatives ||
      (l.creatives && l.creatives.length === 0)
    ) {
      return true;
    }
    return false;
  });
}

/**
 * @method getFormattedLocationData
 * @description return formatted location data to save in DB
 * @param {*} values
 * @param {*} campaign
 * @returns
 */
function getFormattedLocationData(
  values = {},
  campaign,
  selectedBrand = undefined
) {
  const {
    locations: { subscribed },
  } = campaign;

  const locs = Object.keys(values);

  if (locs.length === 0) return {};

  const fullyFilledLocationsIds = locs.length
    ? locs.filter((key) => {
        if (isFullyFilledValidLocation(values[key], campaign)) {
          return true;
        }
        return false;
      })
    : [];

  // As array
  return [
    ...fullyFilledLocationsIds.reduce((acc, key) => {
      const locData = subscribed.find((l) => String(l.id) === String(key));

      if (locData) {
        const formattedLocationValues = { ...values[key] };
        delete formattedLocationValues.budgetRecommendation;
        delete formattedLocationValues.budgetRecommendationKey;
        delete formattedLocationValues.budgetRecommendationsByKey;
        delete formattedLocationValues.isBudgetRecommendationLoading;
        const budgetRecommendationData = isBudgetRecommendationEnabled({
          campaign,
          selectedBrand,
        })
          ? getBudgetRecommendationSubmitData(values[key])
          : undefined;
        const channelSelectionEnabled =
          isParticipantChannelSelectionEnabled(campaign);
        const selectedChannelIds = values[key].channels?.length
          ? values[key].channels
          : getDefaultSelectedChannelIds(
              locData.creatives,
              values[key].creatives
            );
        const creativeData = channelSelectionEnabled
          ? buildSelectedPackageCreativeData({
              creatives: locData.creatives,
              selectedPackageIds: values[key].creatives,
              selectedChannelIds,
            })
          : values[key].creatives
              .map((cid) => {
                const creative = locData.creatives.find(
                  (cr) => cr.creative_id === cid
                );

                if (!creative) return undefined;
                return {
                  creative_id: cid,
                  creative_name: creative.creative_name,
                  sys_creative_id: creative?.sys_creative_id || "",
                };
              })
              .filter((d) => d);

        return [
          ...acc,
          {
            ...formattedLocationValues,
            locationId: key,
            locationName: locData.location_name,
            system_generated_location_id: locData.system_generated_location_id,
            creativeData,
            ...(channelSelectionEnabled
              ? buildChannelSelectionData({
                  creatives: locData.creatives,
                  selectedPackageIds: values[key].creatives,
                  selectedChannelIds,
                })
              : {}),
            amount: values[key].budget,
            ...(budgetRecommendationData
              ? { budgetRecommendation: budgetRecommendationData }
              : {}),
            location_fees_covered: locData.location_fees_covered,
          },
        ];
      }
      return acc;
    }, []),
  ];

  // As object
  // return {
  //   ...fullyFilledLocationsIds.reduce((acc, key) => {
  //     const locData = subscribed.find((l) => String(l.id) === String(key));
  //

  //     if (locData) {
  //       return {
  //         ...acc,
  //         [key]: {
  //           ...values[key],
  //           location_id: key,
  //           location_name: locData.location_name,
  //           system_generated_location_id: locData.system_generated_location_id,
  //           creativeData: values[key].creatives.map((cid) => {
  //             const creative = locData.creatives.find(
  //               (cr) => cr.creative_id === cid
  //             );
  //             return {
  //               creative_id: cid,
  //               creative_name: creative.creative_name,
  //             };
  //           }),
  //         },
  //       };
  //     }
  //     return acc;
  //   }, {}),
  // };
}

/**
 * @method isAnyLocationHasZeroBudget
 * @description check is there any given location has budget set to $0
 * @param {*} values
 * @returns
 */
// function isAnyLocationHasZeroBudget(values = {}) {
//   const locs = Array.isArray(values) ? values : Object.values(values);

//   return locs.some((l) => parseFloat(l.budget) === 0);
// }

/**
 * @method isNormalCampaignConstraintViolated
 * @description check for Normal campaign constraints and set error if any and stop submit
 * @param {*} campaign
 * @param {*} values
 * @param {*} setFormError
 * @returns
 */
function isNormalCampaignConstraintViolated(
  campaign,
  values = {},
  setFormError
) {
  const filledLocations = Array.isArray(values)
    ? values
    : Object.values(values);

  const { budget_type, min_budget, max_budget, locations } = campaign;

  // if (campaign_type !== "Normal") {
  // }

  const subscribedLocations = locations?.subscribed;

  if (budget_type === "Location Specific") {
    const isOutOfRange = filledLocations.some((l) => {
      if (
        Number(l.budget) &&
        (Number(l.budget) > max_budget || Number(l.budget) < min_budget)
      ) {
        return true;
      }
      return false;
    });
    if (isOutOfRange) {
      // The amount is out of range somewhere
      setFormError({
        message: `Please enter location budget between $${min_budget} & $${max_budget} only`,
      });
      return true;
    }
  } else if (budget_type === "Pre-set") {
    const totalPresetBudget = locations.subscribed.reduce(
      (t, l) => (l.budget ? parseFloat(l.budget?.budget) + t : t),
      0
    );

    const totalFilledBudget = filledLocations.reduce(
      (t, l) => (Number(l.budget) ? parseFloat(l.budget) + t : t),
      0
    );
    if (subscribedLocations.length !== filledLocations.length) {
      setFormError({
        message: `Some location(s) are filled partially. Please fill for all the locations`,
      });
      return true;
    } else if (totalFilledBudget !== totalPresetBudget) {
      setFormError({
        message: `The sum of location's budget must be equal to the sum of pre-set budget $${totalPresetBudget}`,
      });
      return true;
    }
  }
  return false;
}

/**
 * @method isEvergreenCampaignConstraintViolated
 * @description check for Evergreen campaign constraints and set error if any and stop submit
 * @param {*} campaign
 * @param {*} values
 * @param {*} setFormError
 * @returns
 */
function isEvergreenCampaignConstraintViolated(
  campaign,
  values = {},
  setFormError
  // rowValues
) {
  const filledLocations = Array.isArray(values)
    ? values
    : Object.values(values);

  const {
    budget_type,
    min_budget,
    max_budget,
    locations,
    evergreen_participant_date_flag,
  } = campaign;

  // if (campaign_type !== "Evergreen") {
  // }

  const subscribedLocations = locations?.subscribed;

  let invalidDateLoc = "";

  if (budget_type === "Location Specific") {
    const isOutOfRange = filledLocations.some((l) => {
      let budgetMultiplyFactor = 1;
      if (evergreen_participant_date_flag) {
        // If Participant date select is true. We required to calculate new factor for min max budget
        budgetMultiplyFactor = getRequiredMinMaxFactor(
          l.start_date,
          l.end_date
        );
      }

      if (
        Number(l.budget) &&
        (Number(l.budget) > max_budget * budgetMultiplyFactor ||
          Number(l.budget) < min_budget * budgetMultiplyFactor)
      ) {
        const locDetail = subscribedLocations.find(
          (lc) => String(lc.id) === String(l.locationId)
        );
        invalidDateLoc = locDetail?.location_name;
        return true;
      }
      return false;
    });

    if (isOutOfRange) {
      // The amount is out of range somewhere
      setFormError({
        message: evergreen_participant_date_flag
          ? "There are some location(s) with out of range budget."
          : `Please enter location budget between $${min_budget} & $${max_budget} only`,
      });
      return true;
    }

    const isInvalidStartDate = filledLocations.some((l) => {
      const locDetail = subscribedLocations.find(
        (lc) => String(lc.id) === String(l.locationId)
      );
      invalidDateLoc = locDetail?.location_name;

      const { minStartDate, maxStartDate } = getCampaignMinMaxStartDate(
        locDetail,
        campaign
      );
      const { minEndDate, maxEndDate } = getCampaignMinMaxEndDate(
        l.start_date,
        locDetail,
        campaign
      );
      if (!evergreen_participant_date_flag) {
        return false;
      }
      if (
        moment(l.start_date).isBefore(minStartDate, "day") &&
        evergreen_participant_date_flag
      ) {
        setFormError({
          message: `Please enter valid start date for the location ${locDetail?.location_name}`,
        });
        return true;
      }
      if (moment(l.start_date).isAfter(maxStartDate, "day")) {
        setFormError({
          message: `Please enter valid start date for the location ${locDetail?.location_name}`,
        });
        return true;
      }
      if (moment(l.end_date).isBefore(minEndDate, "day")) {
        setFormError({
          message: `Please enter valid end date for the location ${locDetail?.location_name}`,
        });
        return true;
      }
      if (moment(l.end_date).isAfter(maxEndDate, "day")) {
        setFormError({
          message: `Please enter valid end date for the location ${locDetail?.location_name}`,
        });
        return true;
      }

      return false;
    });
    if (isInvalidStartDate && evergreen_participant_date_flag) {
      setFormError({
        message: `Please enter valid date for the location ${invalidDateLoc}`,
      });
      return true;
    }
  } else if (budget_type === "Pre-set") {
    const totalPresetBudget = locations.subscribed.reduce(
      (t, l) => (l.budget ? parseFloat(l.budget?.budget) + t : t),
      0
    );

    const totalFilledBudget = filledLocations.reduce(
      (t, l) => (Number(l.budget) ? parseFloat(l.budget) + t : t),
      0
    );

    if (subscribedLocations.length !== filledLocations.length) {
      setFormError({
        message: `Some location(s) are filled partially. Please fill for all the locations`,
      });
      return true;
    } else if (totalFilledBudget !== totalPresetBudget) {
      setFormError({
        message: `The sum of location's budget must be equal to the sum of pre-set budget $${totalPresetBudget}`,
      });
      return true;
    }
  }
  return false;
}

/**
 * @method validateMinCreativeSelection
 * @param {*} campaign
 * @param {*} values
 * @param {*} setFormError
 */
function validateMinCreativeSelection(campaign, values, setFormError) {
  const filledLocations = Array.isArray(values)
    ? values
    : Object.values(values);

  const { creative_select_flag, creative_select_min_value } = campaign;

  const isInvalid = filledLocations.some((l) => {
    if (
      creative_select_flag &&
      l?.creatives?.length < creative_select_min_value
    ) {
      return true;
    }
    return false;
  });

  if (isInvalid) {
    setFormError({
      message: `There some location(s) having less than min ${creative_select_min_value} selected creatives. Please select min ${creative_select_min_value} creatives for a location.`,
    });
    return true;
  }
  return false;
}

/**
 * @method getInitialValues
 * @description make initial values for the join campaign form
 * @param {*} campaign
 * @returns
 */
function getInitialValues(campaign) {
  if (!campaign) return {};

  const {
    start_date,
    end_date,
    participant_campaign,
    locations: { subscribed },
    campaign_type,
    budget_type,
    evergreen_participant_date_flag,
    ability_to_set_budget,
    brand_contribution,
    brand_contribution_type,
    contribution_max_limit,
    contribution_percentage,
    creative_select_flag,
    creative_select_max_value,
  } = campaign;

  let startDate = start_date;
  let endDate = end_date;

  let locationsData = {};

  locationsData = subscribed.reduce((acc, l) => {
    let budget = 0;
    let creatives = [];
    let channels = [];

    const locationDraftData = participant_campaign
      ? participant_campaign.locationData.find(
          (dl) => String(dl.locationId) === String(l.id)
        )
      : null;

    if (budget_type === "Pre-set") {
      /* For preset we always take budget from the budget set by admin, even if there is draft data */
      budget = l.budget?.budget || 0;

      /* if there is budget in draft the consider that (Check for total sum at the time of submit, that should be valid even the location budget is altered by the admin i.e. When altered new sum will be according to the new location's budget)
       */
      if (locationDraftData && ability_to_set_budget) {
        budget = locationDraftData.budget;
      }
    } else if (locationDraftData) {
      // if budget type is Location Specific and there is draft data
      budget = locationDraftData.budget;
    }

    // to set initially selected creative
    if (locationDraftData) {
      const onlyAvailableInCreativeList =
        locationDraftData?.creatives?.filter((c) =>
          l?.creatives?.find((cr) => String(cr.creative_id) === String(c))
        ) || [];

      if (creative_select_flag) {
        if (locationDraftData?.creatives.length > creative_select_max_value) {
          creatives = [];
        } else {
          creatives = onlyAvailableInCreativeList;
        }
      } else {
        creatives = onlyAvailableInCreativeList;
      }
    } else {
      const defaultCreative =
        l.creatives?.find((creative) => creative?.is_recommended) ||
        l.creatives?.[0];
      if (defaultCreative?.creative_id) {
        creatives.push(defaultCreative.creative_id);
      }
    }

    const canSelectSpecificChannels =
      isParticipantChannelSelectionEnabled(campaign);
    if (canSelectSpecificChannels) {
      const availableChannelIds = getPackageChannelOptions(l.creatives).map(
        (channel) => channel.id
      );
      const draftChannels = getDraftSelectedChannelIds(locationDraftData);

      channels = draftChannels.length
        ? draftChannels.filter((channel) =>
            availableChannelIds.includes(channel)
          )
        : getDefaultSelectedChannelIds(l.creatives, creatives);
    }

    if (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
      // for Evergreen take start and end date from location
      startDate = l.campaignStartDate;
      endDate = l.campaignEndDate;

      if (evergreen_participant_date_flag) {
        /*
        startDate = moment().add(evergreen_participant_min_value, "month");
        endDate = moment().add(evergreen_participant_min_value, "month");
        */
        startDate = null;
        endDate = null;
        if (locationDraftData) {
          startDate = locationDraftData.start_date;
          endDate = locationDraftData.end_date;

          const { minStartDate } = getCampaignMinMaxStartDate(l, campaign);

          if (
            moment(locationDraftData.start_date).isBefore(
              minStartDate,
              "day"
            ) ||
            !moment(locationDraftData.start_date).isValid()
          ) {
            // flushed saved dates if it is before 5 business days
            startDate = null;
            endDate = null;
          }
        }

        // if location fees covered has changed then we need to set start and end date to null only for evergreen campaign having participant date flag ON

        if (
          l?.location_fees_covered !== locationDraftData?.location_fees_covered
        ) {
          startDate = null;
          endDate = null;
        }
      }
    }

    let brand_contribution_amount = 0;
    if (brand_contribution) {
      if (brand_contribution_type && brand_contribution_type === "preset") {
        brand_contribution_amount =
          l?.budget?.brand_match_preset_amount * 1 || 0;
      } else {
        brand_contribution_amount = getBrandContributionAmount({
          amount: budget,
          brand_contribution,
          brand_contribution_type,
          contribution_max_limit,
          contribution_percentage,
          brand_match_preset_amount:
            l?.budget?.brand_match_preset_amount * 1 || 0,
        });
      }
    }

    const initialLocationData = {
      start_date: startDate,
      end_date: endDate,
      budget: budget || 0,
      creatives: creatives || [],
      ...(canSelectSpecificChannels ? { channels } : {}),
      brand_contribution_amount: brand_contribution_amount,
      advance_setting: locationDraftData?.advance_setting,
      location_fees_covered: l?.location_fees_covered,
    };

    return {
      ...acc,
      [l.id]: {
        ...initialLocationData,
        ...(locationDraftData?.budgetRecommendation
          ? buildBudgetRecommendationState({
              locFormData: initialLocationData,
              recommendation: locationDraftData.budgetRecommendation,
            })
          : {}),
      },
    };
  }, {});

  return locationsData;
}

/**
 * @method canChangeDate
 * @description check whether user can enter/change dates
 * @param {*} campaign
 * @returns
 */
function canChangeDate(campaign) {
  if (!campaign) return false;
  const { campaign_type, evergreen_participant_date_flag } = campaign;
  if (campaign_type === CAMPAIGN_CONST.TYPE.NORMAL) {
    return false;
  } else if (!evergreen_participant_date_flag) {
    return false;
  }

  return true;
}

/**
 * @method canChangeBudget
 * @description check whether user can enter/change budget
 * @param {*} campaign
 * @returns
 */
// function canChangeBudget(campaign) {
//   if (!campaign) return false;
//   const { ability_to_set_budget } = campaign;
//   if (!ability_to_set_budget) {
//     return false;
//   }
//   return true;
// }

/**
 * @method getBrandContributionAmount
 * @description return brand brand contribution amount for a location
 * @param {*} amount
 * @param {*} brand_contribution
 * @param {*} contribution_percentage
 * @param {*} contribution_max_limit
 * @param {*} brand_contribution_type
 * @param {*} brand_match_preset_amount
 * @returns
 */
function getBrandContributionAmount({
  amount,
  brand_contribution,
  brand_contribution_type,
  contribution_percentage,
  contribution_max_limit,
  brand_match_preset_amount,
}) {
  if (!brand_contribution || brand_contribution === 0) return 0;

  if (brand_contribution_type && brand_contribution_type === "preset") {
    return parseFloat(brand_match_preset_amount);
  }

  let contributionAmount = 0;

  if (contribution_max_limit && contribution_percentage) {
    const brandPercent = (amount * 1 * contribution_percentage) / 100;
    contributionAmount =
      contribution_max_limit < brandPercent
        ? contribution_max_limit
        : brandPercent;
  } else if (contribution_percentage) {
    contributionAmount = (amount * 1 * contribution_percentage) / 100;
  } else if (contribution_max_limit) {
    contributionAmount = contribution_max_limit;
  }

  return parseFloat(String(contributionAmount));
}

/**
 * @method getTotalCalculations
 * @description return total calculations for confirm page
 * @param {*} campaign
 * @returns
 */
function getTotalCalculations(campaign) {
  const {
    brand_contribution,
    contribution_percentage,
    contribution_max_limit,
    brand_contribution_type,
    participant_campaign,
    // enable_payment_through_portal,
    // locations,
  } = campaign;
  let brandContribution = 0;

  // const subscribedLocations = locations?.subscribed;

  // const country = subscribedLocations?.some(
  //   (l) => l?.country?.toLocaleLowerCase() === "canada"
  // )
  //   ? "canada"
  //   : subscribedLocations?.[0]?.country;

  participant_campaign?.locationData?.forEach((l) => {
    brandContribution += getBrandContributionAmount({
      amount: l.budget * 1,
      brand_contribution,
      brand_contribution_type,
      contribution_percentage,
      // contribution_max_limit:
      //   contribution_max_limit *
      //   (participant_campaign?.locationData?.length || 1),
      contribution_max_limit: contribution_max_limit,
      brand_match_preset_amount: l.brand_contribution_amount * 1,
    });
  });

  // campaign budget is sum of locations budget + brand contribution
  const totalCampaignBudget =
    participant_campaign?.total_budget + brandContribution;

  // just to show
  const budgetYouPay = participant_campaign?.total_budget;

  // calculate credit card processing fee on sum of locations budget only
  // const creditCardProcessingFee = enable_payment_through_portal
  //   ? getCreditCardProcessingFee(budgetYouPay, country)
  //   : 0;

  // final amount
  // const amountYouPay = budgetYouPay + creditCardProcessingFee;
  const amountYouPay = budgetYouPay;

  return {
    totalCampaignBudget,
    brandContribution,
    budgetYouPay,
    // creditCardProcessingFee,
    amountYouPay: amountYouPay ? parseFloat(amountYouPay.toFixed(2)) : 0,
  };
}

/**
 * @method getRequiredMinMaxFactor
 * @description return weekly min/max budget multiplier for selected dates
 * @param {*} start_date
 * @param {*} end_date
 * @returns
 */
function getRequiredMinMaxFactor(start_date, end_date) {
  if (!start_date || !end_date) return 1;
  const s = moment(start_date);
  const e = moment(end_date);
  if (s.isValid() && e.isValid()) {
    const inclusiveFlightDays = e.diff(s, "days") + 1;
    const weeks = Math.ceil(inclusiveFlightDays / 7);

    return weeks <= 0 ? 1 : weeks;
  }
  return 1;
}

/**
 * @method getRequiredMinMax
 * @description return required min & max amount when budget type is location specific
 * @param {*} locFormData
 * @param {*} campaign
 * @returns
 */
function getRequiredMinMax(locFormData, campaign) {
  const {
    campaign_type,
    budget_type,
    evergreen_participant_date_flag,
    min_budget,
    max_budget,
  } = campaign;

  if (budget_type === "Location Specific") {
    let minRequiredBudget = min_budget;
    let maxRequiredBudget = max_budget;
    const enteredBudget = Number(locFormData.budget) || 0;
    if (enteredBudget === 0 || enteredBudget) {
      if (
        campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN &&
        evergreen_participant_date_flag
      ) {
        const factor = getRequiredMinMaxFactor(
          locFormData.start_date,
          locFormData.end_date
        );

        minRequiredBudget = min_budget * factor;
        maxRequiredBudget = max_budget * factor;
        if (
          locFormData.start_date &&
          locFormData.end_date &&
          (enteredBudget > maxRequiredBudget ||
            enteredBudget < minRequiredBudget)
        ) {
          return {
            minRequiredBudget,
            maxRequiredBudget,
          };
        }
      } else if (
        enteredBudget > maxRequiredBudget ||
        enteredBudget < minRequiredBudget
      ) {
        return {
          minRequiredBudget,
          maxRequiredBudget,
        };
      }
    }
  }
  return null;
}

/** Post Card Campaign */

function getPostCardValidLocations(
  values = {},
  campaign,
  returnLocationsAsArray = true
) {
  const locs = Object.keys(values);
  if (locs.length === 0)
    return {
      locIds: [],
      locs: returnLocationsAsArray ? [] : {},
    };

  const fullyFilledLocationsIds = locs.length
    ? locs.filter((key) => {
        if (isFullyFilledPostCardLocation(values[key])) {
          return true;
        }
        return false;
      })
    : [];

  let fullyFilledLocations = {
    ...fullyFilledLocationsIds.reduce(
      (acc, key) => ({ ...acc, [key]: values[key] }),
      {}
    ),
  };

  if (returnLocationsAsArray) {
    fullyFilledLocations = Object.values(fullyFilledLocations);
  }

  return {
    locIds: fullyFilledLocationsIds,
    locs: fullyFilledLocations,
  };
}

function getPostCardLocTotal(values = {}) {
  const locs = Array.isArray(values) ? values : Object.values(values);
  let budgetTotal = 0.0;
  if (locs.length) {
    budgetTotal = locs.reduce(
      (t, v) =>
        (Number(v?.bulkDropCost) || 0) + (parseFloat(v?.mailingCost) || 0) + t,
      0.0
    );
    // brandContributionTotal = locs.reduce(
    //   (t, v) =>
    //     Number(v.brand_contribution_amount)
    //       ? parseFloat(v.brand_contribution_amount) + t
    //       : t,
    //   0.0
    // );
  }
  return budgetTotal;
}

function getPostCardCampFormattedLocationData(values = {}, campaign) {
  try {
    const {
      locations: { subscribed },
    } = campaign;

    const locations = Object.keys(values);

    if (locations.length === 0) return {};

    const { locIds: fullyFilledLocationsIds } = getPostCardValidLocations(
      values,
      campaign,
      true
    );

    // As array
    return [
      ...fullyFilledLocationsIds.reduce((acc, key) => {
        const locData = subscribed.find((l) => String(l.id) === String(key));

        if (locData) {
          const creativeData = values[key].creatives.map((cid) => {
            const creative = locData.creatives.find(
              (cr) => cr.creative_id === cid
            );
            return {
              creative_id: cid,
              creative_name: creative.creative_name,
              sys_creative_id: creative?.sys_creative_id || "",
              attachment: creative.package_channels?.map(
                (channel) => channel.attachment
              ),
            };
          });
          const isMailing =
            values[key]?.isMailing && values[key]?.mailingQnt ? true : false;

          const isBulkDrop =
            values[key]?.isBulkDrop && values[key]?.bulkDropQnt ? true : false;

          return [
            ...acc,
            {
              ...values[key],
              locationId: key,
              locationName: locData.location_name,
              system_generated_location_id:
                locData.system_generated_location_id,
              creativeData,

              postcard_address: isMailing
                ? values[key]?.postcard_address || ""
                : "",
              isMailing: isMailing,
              mailingType: isMailing ? values[key]?.mailingType : "",
              mailingQnt: values[key]?.mailingQnt,
              mailingCost: values[key]?.mailingCost,
              isBulkDrop: isBulkDrop,
              bulkDropQnt: values[key]?.bulkDropQnt,
              bulkDropCost: values[key]?.bulkDropCost,
              // schedule_date: moment(values[key]?.week).isValid()
              //   ? moment(values[key]?.week)
              //       .startOf("week")
              //       .add(2, "day")
              //       .startOf("isoWeek")
              //       .format("YYYY-MM-DD")
              //   : "",
              schedule_date: values[key]?.week
                ? moment(values[key]?.week).isValid()
                  ? moment(values[key]?.week)
                      .startOf("isoWeek")
                      .format("YYYY-MM-DD")
                  : ""
                : values[key]?.schedule_date,
              amount:
                (values[key]?.mailingCost || 0) +
                (values[key]?.bulkDropCost || 0),
              budget:
                (values[key]?.mailingCost || 0) +
                (values[key]?.bulkDropCost || 0),
            },
          ];
        }
        return acc;
      }, []),
    ];
  } catch (err) {
    logger.error("getPostCardCampFormattedLocationData error", err);
  }
}

function getLocAddress(loc) {
  return `${loc?.street_address || ""}${loc?.city ? `, ${loc?.city}` : ""}${
    loc.state ? `, ${loc.state}` : ""
  }${loc.zip_code ? `, ${loc.zip_code}` : ""}`;
}

function getPostCardCampInitialValues(campaign) {
  if (!campaign) return {};

  const {
    participant_campaign,
    locations: { subscribed },
    campaign_type,
  } = campaign;

  let locationsData = {};

  locationsData = subscribed.reduce((acc, l) => {
    const locationDraftData = participant_campaign
      ? participant_campaign.locationData.find(
          (dl) => String(dl.locationId) === String(l.id)
        )
      : null;

    /** Schedule date */
    let schedule_date = campaign?.schedule_date || "";

    if (campaign_type === CAMPAIGN_CONST.POSTCARD.TYPE.EVERGREEN) {
      const sevenWeekFromNow = moment()
        .add(7, "week")
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      schedule_date = sevenWeekFromNow;

      if (locationDraftData && locationDraftData?.schedule_date) {
        if (
          moment(locationDraftData?.schedule_date).isValid() &&
          moment(locationDraftData?.schedule_date).isAfter(
            moment(sevenWeekFromNow),
            "day"
          )
        ) {
          schedule_date = locationDraftData?.schedule_date;
        }
      }
    }

    return {
      ...acc,
      [l.id]: {
        ...locationDraftData,
        schedule_date,
      },
    };
  }, {});

  return locationsData;
}

function isPostCardCostFactorExist(campaign) {
  if (!campaign?.locations?.subscribed?.length) {
    return true;
  }
  if (
    !campaign?.postcard_cost_mailings?.length &&
    !campaign?.postcard_cost_shippings?.length
  ) {
    return false;
  }
  return true;
}

function isCampaignAdvancedSettingsViolated(
  campaign,
  locationData,
  setFormError
) {
  const { campaign_advanced_setting } = campaign;

  const { is_required, settings } = campaign_advanced_setting || {};

  if (!settings?.length || !is_required) {
    return false;
  }

  if (!locationData.length) {
    setFormError({
      message: `At least one valid filled location is required`,
    });
    return true;
  }

  let isAdvancedSettingsNotFilled = locationData?.some((l) => {
    const { advance_setting } = l;
    if (!advance_setting?.length) {
      return true;
    }
    return false;
  });

  if (isAdvancedSettingsNotFilled) {
    // setFormError({
    //   message: `Please fill Advanced Settings for each valid locations you have filled in the form.`,
    // });
    return true;
  }

  return false;
}

/**
 * @method getCampaignMinMaxStartDate
 * @description Calculate the minimum & maximum allowed start date based on business days and subscription
 * @param {Object} subscription The location's subscription object
 * @returns {Object} Moment object representing the minimum & maximum start date
 */
export function getCampaignMinMaxStartDate(loc, campaign) {
  const {
    evergreen_participant_date_flag,
    evergreen_available_after_completion_flag,
    evergreen_participant_min_value,
    campaign_type,
    start_date,
    availability_window,
    availability_window_date,
    // allow_multiple_enrollments_per_window,
  } = campaign;

  if (campaign_type === CAMPAIGN_CONST.TYPE.NORMAL) {
    return {
      minStartDate: moment(start_date),
      maxStartDate: moment(start_date),
    };
  }

  if (
    !evergreen_participant_date_flag &&
    evergreen_available_after_completion_flag
  ) {
    return {
      minStartDate: moment(loc?.campaignStartDate),
      maxStartDate: moment(loc?.campaignStartDate),
    };
  }

  const { subscription, location_fees_covered } = loc;
  const { subscription_start_date, subscription_end_date } = subscription || {};

  const minDuration = Number(evergreen_participant_min_value) - 1 || 0;

  const isAvailableWindow =
    availability_window &&
    availability_window !==
      CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE;

  // launch buffer day calculation
  const today = moment();
  const bufferDays = getCampaignLaunchBufferDays();

  const businessStart = isBusinessDay(today)
    ? today
    : nextBusinessDay(today.clone());

  const baseMinStart = businessAdd(businessStart, bufferDays);

  const minStartDate = moment.max(
    baseMinStart,
    !location_fees_covered ? moment(subscription_start_date) : baseMinStart,
    isAvailableWindow
      ? moment(availability_window_date.window_start_date)
      : baseMinStart
  );

  const latestEnd = moment.min(
    !location_fees_covered
      ? moment(subscription_end_date)
      : moment(DATE_CONST.INFINITY_DATE),
    isAvailableWindow
      ? moment(availability_window_date.window_end_date)
      : moment(DATE_CONST.INFINITY_DATE)
  );

  const maxStartDate = latestEnd.clone().subtract(minDuration, "days");

  return {
    minStartDate,
    maxStartDate,
  };
}

/**
 * @method getCampaignMinMaxEndDate
 * @description Calculate the minimum & maximum allowed end date based on min and max days allowed in campaign settings and subscription
 * @param {Object} subscription The location's subscription object
 * @returns {Object} Moment object representing the minimum & maximum end date
 */
export function getCampaignMinMaxEndDate(startDate, loc, campaign) {
  const {
    evergreen_participant_date_flag,
    evergreen_available_after_completion_flag,
    evergreen_participant_min_value,
    evergreen_participant_max_value,
    campaign_type,
    end_date,
    availability_window,
    availability_window_date,
    // allow_multiple_enrollments_per_window,
  } = campaign;

  if (campaign_type === CAMPAIGN_CONST.TYPE.NORMAL) {
    return {
      minEndDate: moment(end_date),
      maxEndDate: moment(end_date),
    };
  }

  if (
    !evergreen_participant_date_flag &&
    evergreen_available_after_completion_flag
  ) {
    return {
      minEndDate: moment(loc?.campaignEndDate),
      maxEndDate: moment(loc?.campaignEndDate),
    };
  }

  const { subscription, location_fees_covered } = loc;
  const { subscription_end_date } = subscription || {};

  const minDuration = Number(evergreen_participant_min_value) - 1 || 0;
  const maxDuration =
    Number(evergreen_participant_max_value) - 1 || minDuration;

  const isAvailableWindow =
    availability_window &&
    availability_window !==
      CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE;

  const minEndDate = moment(startDate).clone().add(minDuration, "days");

  const latestEnd = moment.min(
    !location_fees_covered
      ? moment(subscription_end_date)
      : moment(DATE_CONST.INFINITY_DATE),
    isAvailableWindow
      ? moment(availability_window_date.window_end_date)
      : moment(DATE_CONST.INFINITY_DATE)
  );

  const maxEndDate = moment.min(
    moment(startDate).clone().add(maxDuration, "days"),
    latestEnd
  );

  return {
    minEndDate,
    maxEndDate,
  };
}

function validateEvergreenCampLocForLocSpecBudgetType(
  campaign,
  locData,
  locDetail
) {
  const {
    budget_type,
    min_budget,
    max_budget,
    evergreen_participant_date_flag,
  } = campaign;

  const { budget, start_date, end_date } = locData;

  let invalidLoc = locDetail?.location_name;
  let isInvalidBudget = false;
  let isInvalidDate = false;

  const { minStartDate, maxStartDate } = getCampaignMinMaxStartDate(
    locDetail,
    campaign
  );
  const { minEndDate, maxEndDate } = getCampaignMinMaxEndDate(
    start_date,
    locDetail,
    campaign
  );

  if (budget_type === CAMPAIGN_CONST.BUDGET_TYPE.LOCATION_SPECIFIC) {
    let budgetMultiplyFactor = 1;
    if (evergreen_participant_date_flag) {
      // If Participant date select is true. We required to calculate new factor for min max budget
      budgetMultiplyFactor = getRequiredMinMaxFactor(start_date, end_date);
    }

    if (
      Number(budget) &&
      (Number(budget) > max_budget * budgetMultiplyFactor ||
        Number(budget) < min_budget * budgetMultiplyFactor)
    ) {
      isInvalidBudget = true;
    }

    if (
      moment(start_date).isBefore(minStartDate, "day") ||
      moment(start_date).isAfter(maxStartDate, "day")
    ) {
      isInvalidDate = true;
    }
    if (
      moment(end_date).isBefore(minEndDate, "day") ||
      moment(end_date).isAfter(maxEndDate, "day")
    ) {
      isInvalidDate = true;
    }
  }
  return {
    isInvalidBudget,
    isInvalidDate,
    invalidLoc,
    minStartDate: moment(minStartDate).format("YYYY-MM-DD"),
    maxStartDate: moment(maxStartDate).format("YYYY-MM-DD"),
    minEndDate: moment(minEndDate).format("YYYY-MM-DD"),
    maxEndDate: moment(maxEndDate).format("YYYY-MM-DD"),
  };
}

function getCollectedMinMaxStartEndDate(
  subscribedLocations,
  campaign,
  formData
) {
  let minSDate = [];
  let maxSDate = [];
  let minEDate = [];
  let maxEDate = [];

  // First collect all start dates
  subscribedLocations.forEach((l) => {
    const { minStartDate, maxStartDate } =
      joinCampaignHelper.getCampaignMinMaxStartDate(l, campaign);
    minSDate.push(minStartDate);
    maxSDate.push(maxStartDate);
  });

  // Calculate the overall min/max start dates
  const overallMinStartDate = moment(min(minSDate.map((d) => d.valueOf())));
  const overallMaxStartDate = moment(max(maxSDate.map((d) => d.valueOf())));

  // Then calculate end dates using the overall start date info
  subscribedLocations.forEach((l) => {
    const { minEndDate, maxEndDate } = getCampaignMinMaxEndDate(
      formData?.startDate || overallMinStartDate,
      l,
      campaign
    );
    minEDate.push(minEndDate);
    maxEDate.push(maxEndDate);
  });

  const dates = {
    minStartDate: overallMinStartDate,
    maxStartDate: overallMaxStartDate,
    minEndDate: moment(min(minEDate.map((d) => d.valueOf()))),
    maxEndDate: moment(max(maxEDate.map((d) => d.valueOf()))),
  };

  // Handle evergreen participant minimum duration requirement
  if (campaign?.evergreen_participant_date_flag) {
    const minRequiredDuration = campaign?.evergreen_participant_min_value;
    // const maxRequiredDuration = campaign?.evergreen_participant_max_value;

    // Ensure minEnd is at least minRequiredDuration days after minStart
    const minEndRequired = moment(dates.minStartDate).add(
      minRequiredDuration,
      "days"
    );

    // const maxEndRequired = moment(dates.maxStartDate).subtract(
    //   maxRequiredDuration,
    //   "days"
    // );

    if (minEndRequired.isAfter(dates.minEndDate, "day")) {
      dates.minEndDate = minEndRequired;
    }

    if (dates.minEndDate.isBefore(minEndRequired, "day")) {
      dates.minEndDate = minEndRequired;
    }

    // Adjust maxStart if needed to ensure minimum duration is possible
    // if (
    //   dates.maxEndDate.diff(dates.maxStartDate, "days") < minRequiredDuration
    // ) {
    //   dates.maxStartDate = moment(dates.maxEndDate).subtract(
    //     minRequiredDuration,
    //     "days"
    //   );
    // }
  }

  // Ensure maxStart is not before minStart
  if (dates.maxStartDate.isBefore(dates.minStartDate, "day")) {
    dates.maxStartDate = moment(dates.minStartDate);
  }

  // Ensure maxEnd is not before minEnd
  if (dates.maxEndDate.isBefore(dates.minEndDate, "day")) {
    dates.maxEndDate = moment(dates.minEndDate);
  }

  // Ensure end dates are not before their corresponding start dates
  // if (dates.maxEndDate.isBefore(dates.maxStartDate, "day")) {
  //   dates.maxEndDate = moment(dates.maxStartDate);
  // }
  return dates;
}

function hideField(campaign) {
  const isNormalCampaign =
    campaign.campaign_type === CAMPAIGN_CONST.TYPE.NORMAL;
  const isPreSetBudget =
    campaign?.budget_type === CAMPAIGN_CONST.BUDGET_TYPE.PRESET;

  // if participant select date is not true then we need to hide start and end date
  const isNotParticipantSelectDate = !campaign?.evergreen_participant_date_flag;

  return {
    startDate: isNormalCampaign || isPreSetBudget || isNotParticipantSelectDate,
    endDate: isNormalCampaign || isPreSetBudget || isNotParticipantSelectDate,
    locationBudget: isPreSetBudget,
  };
}
